import titleCase from 'title-case';
import React from 'react';
import moment from 'moment';
import keyBy from 'lodash/keyBy';
import {
  isIOS,
  isAndroid,
  isMobile,
  isSafari,
  isMobileSafari,
} from 'react-device-detect';

import { FULFILLMENTS, STATUS } from '../constants';
import {
  STATUS_FORMAT,
  DISPLAY_DELIVERY_WINDOW,
} from '../constants/statusformats';
import {
  isInstoreOrderIdValid,
  isNotUndefined,
  isOrderIdValid,
} from './validations';
import { getHostName } from './cookie';
import { hostName, isFeatureSwitchOn } from './window';

export const isDateGMT = (date: String) => {
  if (!date) {
    return false;
  }
  return date.length > 10;
};

export const formatAMPM = (time: string) => {
  const regExAM = new RegExp(' am', 'ig');
  const regExPM = new RegExp(' pm', 'ig');
  const replaceValueAM = ' a.m.';
  const replaceValuePM = ' p.m.';

  return time.replace(regExAM, replaceValueAM).replace(regExPM, replaceValuePM);
};

export const formatInstoreOrder = (orderId: string) => {
  const formatted = orderId.toUpperCase().replace(/[\s-]/g, '');
  return formatted.length >= 5
    ? `${formatted.substring(0, 5)}-${formatted.substring(5, formatted.length)}`
    : formatted;
};

export const formatBossMultiPackage = (orderDetails: Object) => {
  const { orderGroups } = orderDetails;
  orderGroups.forEach((group: Object, index: number) => {
    const { fulfillment, statusCode, shipments } = group;
    if (
      fulfillment === FULFILLMENTS.FULFILLMENT_METHOD_BOSS &&
      statusCode === STATUS.STATUS.COMPLETED &&
      shipments &&
      shipments.length > 1
    ) {
      orderGroups[index].shipments = [shipments[0]];
    }
  });
};

export const formatIsInstore = (orderResponse: Object, isInstore: boolean) => {
  const { orderDetails } = orderResponse;
  orderDetails.isInstore = isInstore;
};

export const formatOrderHistoryPayload = (orderResponse: Object = {}) => {
  const { emailId, orders = [] } = orderResponse;
  /* eslint-disable no-param-reassign */
  const orderActions = orders.reduce((orderPayload: Object, order: Object) => {
    orderPayload[order.orderId] = {
      isFetching: false,
      isError: false,
      isOpen: false,
    };
    return orderPayload;
  }, {});
  /* eslint-enable no-param-reassign */
  return {
    response: {},
    lineItems: {},
    emailId,
    orders,
    orderActions,
  };
};

export const formatOrderDetailsPayload = (
  orderResponse: Object = {},
  isInstore: boolean = false
) => {
  const { orderDetails } = orderResponse;
  const { id, lineItems = {} } = orderDetails;
  const { lineItem = [] } = lineItems;
  formatBossMultiPackage(orderDetails);
  formatIsInstore(orderResponse, isInstore);
  return {
    response: { [id]: orderDetails },
    lineItems: { [id]: keyBy(lineItem, 'lineSeqNum') },
  };
};
/* eslint-disable no-param-reassign */
export const formatEReceiptsPayload = (eReceipts: Array) =>
  eReceipts.reduce((eReceiptsPayload: Object, eReceipt: Object) => {
    eReceiptsPayload[eReceipt.receiptNumber] = {
      isFetching: false,
      isError: false,
      isOpen: false,
    };
    return eReceiptsPayload;
  }, {});
/* eslint-enable no-param-reassign */

export const formatPhoneNumber = (phoneNumber: string) => {
  let formatted = phoneNumber
    .replace(/-/g, '')
    .replace(/\D/g, '')
    .substring(0, 10);
  if (formatted.length > 3 && formatted.length <= 6) {
    formatted = `${formatted.substring(0, 3)}-${formatted.substring(3, 6)}`;
  } else if (phoneNumber.length > 6) {
    formatted = `${formatted.substring(0, 3)}-${formatted.substring(
      3,
      6
    )}-${formatted.substring(6)}`;
  }
  return formatted;
};

export const formatPrice = (val: number): string => {
  if (val >= 0 && val !== null && val !== '') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(val);
  }
  return null;
};

export const formatEReceiptPrice = (val: number) =>
  val &&
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(val);

export const formatCityState = (address: Object): string => {
  if (address) {
    const { city, state } = address;
    return city && state
      ? `${titleCase(city)}, ${state.toUpperCase()}`
      : '----';
  }
  return '----';
};

export const formatOrderStatus = (fulfillment: string, statusCode: number) => {
  // inStore Misc Only
  if (fulfillment === FULFILLMENTS.MISC)
    return statusCode === 'Complete' ? 'Complete' : 'Order Completed';
  // backordered or NA
  if (statusCode === undefined || statusCode === STATUS.STATUS.BACKORDERED)
    return 'Processing';
  // 100
  if (statusCode === STATUS.STATUS.SEE_ORDER_DETAILS)
    return 'See order details';
  if (statusCode === STATUS.STATUS.CANCELED) return 'Canceled';
  if (statusCode === STATUS.STATUS.RETURNED) return 'Returned';
  if (statusCode === STATUS.STATUS.RETURN_IN_PROGRESS)
    return 'Return In Progress';

  // all other cases
  return STATUS.ORDER_STATUS[fulfillment][statusCode];
};

const deliveredAndHasNoDate = (statusMessageArray: array) =>
  statusMessageArray &&
  statusMessageArray[0] &&
  statusMessageArray[0].content &&
  statusMessageArray[0].content === 'Arrived on ' &&
  statusMessageArray[1] &&
  !statusMessageArray[1].content;

export const formatOrderStatusMessage = (
  statusCode: number,
  fulfillment: string,
  orderStatusParams: Object
) => {
  const {
    deliveryDate,
    timeWindowPayload,
    multiDateEnd,
    is121Days,
  } = orderStatusParams;
  const statusMessageArray = [];
  const inline = 'u__inline-block';
  const inlinePre = 'u__inline-block u__white-space-pre';

  if (statusCode === STATUS.STATUS.BACKORDERED) {
    statusMessageArray.push({
      content: 'Currently Backordered',
      classname: inline,
    });
    return statusMessageArray;
  }
  if (statusCode === STATUS.STATUS.CANCELED) {
    statusMessageArray.push({
      content: 'Your Cancellation Was Successful',
      classname: inline,
    });
    return statusMessageArray;
  }
  if (fulfillment === FULFILLMENTS.MISC) {
    statusMessageArray.push({
      content: 'Purchased in Store',
      classname: inline,
    });
    return statusMessageArray;
  }
  if(isFeatureSwitchOn('ENABLE_PANDEMIC_MESSAGING') &&
    fulfillment === FULFILLMENTS.BOPIS &&
    statusCode === STATUS.STATUS.PROCESSING){
    statusMessageArray.push({
      content: `We'll notify you when it's ready`,
      classname: inline,
    });
    return statusMessageArray;
  }
  const formatString = STATUS_FORMAT[fulfillment][statusCode] || 'dddd, MMMM D';

  // 100 or NA
  if (
    statusCode === undefined ||
    statusCode === STATUS.STATUS.SEE_ORDER_DETAILS ||
    ((fulfillment === FULFILLMENTS.BODFS || fulfillment === FULFILLMENTS.DFS) &&
      !deliveryDate &&
      statusCode !== STATUS.STATUS.DELIVERED)
  ) {
    statusMessageArray.push({
      content: 'Your order is being processed',
      classname: inline,
    });
    return statusMessageArray;
  }
  // 121 Days or Bodfs no shipping info complete
  if (
    is121Days &&
    (fulfillment !== FULFILLMENTS.BODFS || fulfillment !== FULFILLMENTS.DFS)
  ) {
    statusMessageArray.push({
      content: 'Your Items Have Been Delivered',
      classname: inline,
    });
    return statusMessageArray;
  }

  statusMessageArray.push({
    content: STATUS.ORDER_STATUS_MESSAGE[fulfillment][statusCode],
    classname: inlinePre,
  });

  statusMessageArray.push({
    content: deliveryDate
      ? formatAMPM(moment(deliveryDate).format(formatString))
      : '',
    classname: inlinePre,
  });

  if (
    DISPLAY_DELIVERY_WINDOW[fulfillment][statusCode] &&
    timeWindowPayload.deliveryBeginTime &&
    timeWindowPayload.deliveryEndTime
  ) {
    const { deliveryBeginTime, deliveryEndTime } = timeWindowPayload;

    statusMessageArray.push({ content: <br />, classname: '' });

    statusMessageArray.push({
      content: `${deliveryBeginTime} - `,
      classname: inlinePre,
    });

    if (multiDateEnd)
      statusMessageArray.push({
        content: formatAMPM(moment(multiDateEnd).format(formatString)),
        classname: inlinePre,
      });

    statusMessageArray.push({ content: deliveryEndTime, classname: inlinePre });
  }

  if (deliveredAndHasNoDate(statusMessageArray)) {
    statusMessageArray.length = 0;
    statusMessageArray.push({
      content: 'Your items have been delivered',
      classname: inlinePre,
    });
    return statusMessageArray;
  }
  return statusMessageArray;
};

export const formatUrlOrderId = (param: string) => {
  const newParam = param.replace('withTHD', '');
  const orderId = newParam.substring(
    newParam.lastIndexOf('_') + 1,
    newParam.length
  );
  if (isOrderIdValid(orderId) || isInstoreOrderIdValid(orderId)) {
    return orderId;
  }
  return null;
};

export const formatBodfsDeliveryWindow = (date: string) =>
  date ? `${formatAMPM(moment(date).format('h a'))}` : null;

export const formatApplianceDeliveryWindow = (time: string) =>
  time
    ? `${formatAMPM(
        moment
          .utc(time, 'hh:mm A')
          .local()
          .format('h a')
      )}`
    : null;

export const formatBreadCrumbsPath = (
  pathname: string,
  rootName: any = null
) => {
  const paths = [
    {
      name:
        isNotUndefined(rootName) && typeof rootName === 'string'
          ? rootName
          : '/',
      path: '/',
    },
  ];
  if (pathname === '/') return paths;
  pathname.split('/').reduce((prev: string, curr: string) => {
    const currPath = `${prev}/${curr}`;
    paths.push({
      name: curr,
      path: currPath,
    });
    return currPath;
  });
  return paths;
};

export const formatItemUrl = (url: string) => `https://${getHostName()}${url}`;

export const getShippingDeliveryDate = (
  group: Object,
  shipmentIndex: Number
) => {
  const { actions, shipments, statusCode, estDeliveryDate } = group;

  // linehaul items
  if (actions && actions.isLineHaul && shipments) {
    shipments.forEach((shipment: Object) => {
      const { trackingType, actualDeliveryDate } = shipment;
      if (trackingType === 'LastMile') {
        if (statusCode === STATUS.STATUS.DELIVERED && actualDeliveryDate) {
          return actualDeliveryDate;
        }
        return estDeliveryDate;
      }
      return estDeliveryDate;
    });
  }
  if (shipments) {
    const index = typeof shipmentIndex === 'number' ? shipmentIndex : 0;
    const { actualDeliveryDate } = shipments[index];
    if (statusCode === STATUS.STATUS.DELIVERED && actualDeliveryDate) {
      return actualDeliveryDate;
    }
    return estDeliveryDate;
  }

  if (estDeliveryDate) {
    return estDeliveryDate;
  }

  return null;
};

export const formatDeliveryDate = (
  fulfillmentType: String,
  statusCode: Number,
  deliveryDate: String
) => {
  if (
    !deliveryDate ||
    (fulfillmentType === FULFILLMENTS.APPLIANCE &&
      statusCode === STATUS.STATUS.COMPLETED) ||
    (isFeatureSwitchOn('ENABLE_PANDEMIC_MESSAGING') &&
      fulfillmentType === FULFILLMENTS.BOPIS &&
      statusCode === STATUS.STATUS.PROCESSING)
  ) {
    return '';
  }
  if (!isDateGMT(deliveryDate)) {
    const formattedNonGMTDate = moment(deliveryDate).format('MMM. D');
    return formattedNonGMTDate;
  }
  return moment(deliveryDate).format('MMM. D');
};

export const isMultiDateRange = (bodfsDeliveries: object) => {
  if (!bodfsDeliveries) return '';
  const {
    reservationWindowStartTime,
    reservationWindowEndTime,
  } = bodfsDeliveries;
  if (!reservationWindowStartTime && !reservationWindowEndTime) {
    return '';
  }
  if (
    moment(reservationWindowStartTime).format('dddd, MMMM D') !==
    moment(reservationWindowEndTime).format('dddd, MMMM D')
  ) {
    return reservationWindowEndTime;
  }
  return '';
};

export const formatBuyAgainFulfillmentMethod = (fulfillment: String) => {
  if (fulfillment === 'BuyOnlineShipToStore') {
    return 'ShipToStore';
  }
  if (fulfillment === 'BuyOnlinePickupInStore') {
    return 'BOPIS';
  }
  if (fulfillment === 'Appliance') {
    return 'DirectDelivery';
  }
  if (fulfillment === 'BuyOnlineDeliverFromStore') {
    return 'DeliverFromStore';
  }
  return fulfillment;
};

/**
 * Returns true if today is four or more days BEFORE the delivery day
 * @param {string} deliveryDate
 */
export const isFourDaysBefore = (deliveryDate: string) => {
  const currentDeliveryDate = moment(deliveryDate);
  const currentDate = moment().startOf('day');
  return currentDeliveryDate.diff(currentDate, 'days') >= 4;
};

export const formatAddToCalendarUrl = (
  orderId: String,
  deliveryDate: String
) => {
  let calendarUrl = '';
  if (deliveryDate) {
    const event = {
      title: 'Appliance Delivery - The Home Depot',
      description: `Thanks for shopping at the Home Depot! We’ll give you a call on ${moment
        .utc(deliveryDate)
        .subtract(1, 'days')
        .format(
          'MMM. DD'
        )} to confirm your four hour delivery window.\nTo check your order status or make updates to your delivery date, visit https://${hostName()}/order/view/tracking?orderId=${orderId}.\nNeed to reschedule? You can change your appliance delivery date up to two days before your previously scheduled time.\nIf you do reschedule delivery, remember to update this calendar event to get more accurate reminders.`,
      iosDescription: `Thanks for shopping at the Home Depot! We’ll give you a call on ${moment
        .utc(deliveryDate)
        .subtract(1, 'days')
        .format(
          'MMM. DD'
        )} to confirm your four hour delivery window.\\nTo check your order status or make updates to your delivery date, visit https://${hostName()}/order/view/tracking?orderId=${orderId}.\\nNeed to reschedule? You can change your appliance delivery date up to two days before your previously scheduled time.\\nIf you do reschedule delivery, remember to update this calendar event to get more accurate reminders.`,
      startTime: moment.utc(deliveryDate).format('YYYYMMDD'),
      endTime: moment
        .utc(deliveryDate)
        .add(1, 'days')
        .format('YYYYMMDD'),
    };
    if (isIOS && (isSafari || isMobileSafari)) {
      calendarUrl = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `URL:https://${hostName()}/order/view/tracking?orderId=${orderId}`,
        `DTSTART:${event.startTime}`,
        `SUMMARY:${event.title}`,
        `DESCRIPTION:${event.iosDescription}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\n');
      calendarUrl = encodeURI(`data:text/calendar;charset=utf8,${calendarUrl}`);
    } else {
      calendarUrl = 'https://calendar.google.com/calendar/render';
      calendarUrl += '?action=TEMPLATE';
      calendarUrl += `&dates=${event.startTime}/${event.endTime}`;
      calendarUrl += `&text=${encodeURIComponent(event.title)}`;
      calendarUrl += `&details=${encodeURIComponent(event.description)}`;
    }
  }
  return calendarUrl;
};

export const displayAddToCalendar = (
  fulfillmentType: String,
  statusCode: String,
  deliveryDate: String
) =>
  fulfillmentType === FULFILLMENTS.APPLIANCE &&
  (statusCode === STATUS.STATUS.MAJOR_APPLIANCE_ON_SCHEDULE ||
    statusCode === STATUS.STATUS.PROCESSING) &&
  deliveryDate &&
  (isIOS || isAndroid) &&
  isMobile;

export const cleanStoreDetailAddress = (address: string = '') => {
  const regex = /(^|\D)\d{10,}($|\D)/;
  return address.replace(regex, '').trim();
};
